<template>
  <div :id="data.id" class="bg-gray-lighten3 py-10 md:py-14">
    <div class="container overflow-hidden text-slate">
      <UiSectionTitle
        v-if="data.title"
        :title="data.title"
        class="title mb-8 flex whitespace-pre-line text-3xl uppercase leading-none text-slate md:justify-center md:text-center md:text-[3.5rem]"
      />

      <div v-if="items && items.length">
        <swiper-container
          ref="swiperEl"
          init="false"
          class="!overflow-visible pb-11 text-slate"
        >
          <div slot="container-end" class="swiper-scrollbar"></div>

          <swiper-slide
            v-for="(item, i) in items"
            :key="i"
            class="group flex w-[240px] flex-col overflow-hidden rounded-lg bg-white md:w-[324px]"
          >
            <UiLink :to="`/product/${item.slug}`">
              <div>
                <div class="aspect-[324/270]">
                  <CdnImg
                    v-if="item.image"
                    :src="handleImageSrc(item.image)"
                    :alt="handleImageAlt(item.image, item.name)"
                    :width="324"
                    :height="324"
                    class="h-full w-full object-contain"
                    loading="lazy"
                  />
                </div>
              </div>

              <div
                class="flex w-full flex-grow flex-col items-center p-4 text-center md:p-8"
              >
                <div
                  v-if="item.name"
                  class="title mb-2 whitespace-pre-line text-lg uppercase leading-none md:text-xl md:leading-none"
                  v-html="item.name"
                />

                <div
                  v-if="item.description"
                  class="mb-4 whitespace-pre-line text-sm leading-[1.225rem] text-slate md:mb-6"
                  v-html="item.description"
                />

                <div
                  v-if="item.tags && item.tags.length"
                  class="flex flex-wrap gap-1 md:justify-center"
                >
                  <UiTag
                    v-for="(tag, ti) in item.tags"
                    :key="ti"
                    uppercase
                    sans
                    rounded="lg"
                    size="md"
                    color="gray"
                  >
                    <span v-html="tag" />
                  </UiTag>
                </div>
              </div>
            </UiLink>
          </swiper-slide>
        </swiper-container>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import type {
  AppProduct,
  ProductExtended,
} from '@webplatform/frontkit/runtime/types';
import { CdnImg } from '@webplatform/cdn-image';
import { useFetchProducts, useSwiperElement } from '~/composables';
import { getProductContent } from '~/helpers/vue';
import { handleImageSrc, handleImageAlt } from '~/helpers';

interface Props {
  data?: any;
}

const props = withDefaults(defineProps<Props>(), {
  data: () => ({}),
});

const { fetchProductsByIds } = useFetchProducts();
const items = ref<ProductExtended[]>([]);

const swiperOptions = {
  observer: true,
  slidesPerView: 'auto',
  spaceBetween: 16,
  scrollbar: {
    el: '.swiper-scrollbar',
    draggable: true,
    dragSize: 180,
    snapOnRelease: true,
  },
};

const swiperEl = useSwiperElement(swiperOptions);

const setupProduct = (product: AppProduct): ProductExtended => {
  return {
    ...product,
    description: getProductContent(product).description,
    tags: getProductContent(product).tags,
    image: getProductContent(product).image,
  } as ProductExtended;
};

watch(
  () => props.data.productIds,
  async (ids) => {
    if (!ids?.length) return;
    try {
      const data = await fetchProductsByIds(ids as string[]);
      if (data) items.value = data.map(setupProduct);
    } catch {}
  },
  { immediate: true },
);
</script>
